import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FiPlus, FiX, FiUpload } from 'react-icons/fi';

import { useCourseData } from '../hooks/useCourseData';
import { useContentCourse } from '../context/ContentContext';
import apiBackoffice from '../../../../services/apiBackoffice';
import { useToast } from '../../../../hooks/toast';
import { useAdminDelete } from '../../Admin/hooks/useAdminDelete';
import { ContentEditorLayout, Confirm } from '../../../../components/Admin';
import Field from '../../../../components/Admin/Field';
import { TextInput } from '../../../../components/Admin/Field/styles';

import {
  FileRow,
  FileInputLabel,
  HiddenFileInput,
  FileMeta,
  StatusBadge,
  ProgressTrack,
  ProgressFill,
  RemoveButton,
  AddButton,
  FileList,
} from './AttachmentForm.styles';

type RowStatus = 'pending' | 'uploading' | 'done' | 'error';

interface AttachmentRow {
  key: string;
  file: File | null;
  name: string;
  status: RowStatus;
  progress: number;
  errorMessage?: string;
}

interface AttachmentItem {
  attachmentid: string;
  name: string;
  url: string;
  filetype?: string;
  filesize?: number;
}

const ACCEPTED_EXTENSIONS = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.zip';

const STATUS_LABEL: Record<RowStatus, string> = {
  pending: 'Pendente',
  uploading: 'Enviando…',
  done: 'Enviado',
  error: 'Erro',
};

const createEmptyRow = (): AttachmentRow => ({
  key: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
  file: null,
  name: '',
  status: 'pending',
  progress: 0,
});

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AttachmentForm: React.FC = () => {
  const { addToast } = useToast();
  const { courseId } = useContentCourse();
  const {
    seasons,
    movies,
    loadingSeasons,
    loadingMovies,
    selectedCourseId,
    selectedSeasonId,
    selectedMovieId,
    handleSeasonChange,
    handleMovieChange,
  } = useCourseData(courseId);
  const { deleteItem, deletingId } = useAdminDelete();

  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [rows, setRows] = useState<AttachmentRow[]>([createEmptyRow()]);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const moduleOptions = useMemo(
    () =>
      seasons.map((season) => ({
        value: season.seasonid,
        label: season.title,
        hint: season.seasonid,
      })),
    [seasons],
  );

  const lessonOptions = useMemo(
    () =>
      movies.map((movie) => ({
        value: movie.movieid,
        label: movie.title,
        hint: `posição ${movie.position ?? '-'}`,
      })),
    [movies],
  );

  const loadAttachments = useCallback(async () => {
    if (!selectedCourseId || !selectedSeasonId || !selectedMovieId) {
      setAttachments([]);
      return;
    }

    setLoadingAttachments(true);
    try {
      const response = await apiBackoffice.get<AttachmentItem[]>('/backoffice/attachment/list', {
        params: {
          courseid: selectedCourseId,
          seasonid: selectedSeasonId,
          movieid: selectedMovieId,
        },
      });
      setAttachments(Array.isArray(response.data) ? response.data : []);
    } catch {
      setAttachments([]);
    } finally {
      setLoadingAttachments(false);
    }
  }, [selectedCourseId, selectedSeasonId, selectedMovieId]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const pickerItems = useMemo(
    () =>
      attachments.map((attachment) => ({
        id: attachment.attachmentid,
        label: attachment.name,
        hint: attachment.filesize ? formatBytes(attachment.filesize) : attachment.filetype,
      })),
    [attachments],
  );

  const onModuleChange = (seasonId: string) => {
    setSelectedId('');
    handleSeasonChange(seasonId, selectedCourseId);
  };

  const onLessonChange = (movieId: string) => {
    setSelectedId('');
    handleMovieChange(movieId);
  };

  const updateRow = (key: string, patch: Partial<AttachmentRow>) => {
    setRows((previous) =>
      previous.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
  };

  const onFileChange = (key: string, fileList: FileList | null) => {
    const file = fileList?.[0] ?? null;
    if (!file) return;

    setRows((previous) =>
      previous.map((row) =>
        row.key === key
          ? { ...row, file, name: row.name.trim() || file.name, status: 'pending', progress: 0 }
          : row,
      ),
    );
  };

  const onAddRow = () => setRows((previous) => [...previous, createEmptyRow()]);
  const onRemoveRow = (key: string) =>
    setRows((previous) => previous.filter((row) => row.key !== key));

  const onUpload = async () => {
    if (!selectedCourseId || !selectedSeasonId || !selectedMovieId) {
      addToast({
        type: 'error',
        title: 'Selecione módulo e aula',
        description: 'O material pertence a uma aula — escolha módulo e aula antes.',
      });
      return;
    }

    const pending = rows.filter((row) => row.file && row.status !== 'done');
    if (pending.length === 0) {
      addToast({
        type: 'error',
        title: 'Nenhum arquivo',
        description: 'Escolha ao menos um arquivo para enviar.',
      });
      return;
    }

    setUploading(true);
    let success = 0;
    let failure = 0;

    for (const row of pending) {
      const file = row.file as File;
      updateRow(row.key, { status: 'uploading', progress: 0, errorMessage: undefined });

      try {
        const presign = await apiBackoffice.post<{
          attachmentid: string;
          uploadUrl: string;
          fileUrl: string;
        }>('/backoffice/attachment/presign', {
          courseid: selectedCourseId,
          seasonid: selectedSeasonId,
          movieid: selectedMovieId,
          filename: file.name,
          filetype: file.type,
        });

        const { attachmentid, uploadUrl, fileUrl } = presign.data;

        await axios.put(uploadUrl, file, {
          headers: { 'Content-Type': file.type },
          onUploadProgress: (event) => {
            const total = event.total || file.size || 1;
            updateRow(row.key, { progress: Math.round((event.loaded * 100) / total) });
          },
        });

        await apiBackoffice.post('/backoffice/attachment/add', {
          courseid_seasonid_movieid_attachmentid: `${selectedCourseId}_${selectedSeasonId}_${selectedMovieId}_${attachmentid}`,
          courseid: selectedCourseId,
          seasonid: selectedSeasonId,
          movieid: String(selectedMovieId),
          attachmentid,
          name: row.name.trim() || file.name,
          url: fileUrl,
          filetype: file.type,
          filesize: file.size,
        });

        updateRow(row.key, { status: 'done', progress: 100 });
        success += 1;
      } catch (err: any) {
        updateRow(row.key, {
          status: 'error',
          errorMessage: err?.response?.data?.error || err?.message || 'Falha ao enviar.',
        });
        failure += 1;
      }
    }

    if (failure === 0) {
      addToast({
        type: 'success',
        title: 'Materiais enviados',
        description: `${success} arquivo(s) salvos.`,
      });
      setRows([createEmptyRow()]);
      await loadAttachments();
    } else {
      addToast({
        type: 'error',
        title: 'Envio parcial',
        description: `${success} de ${success + failure} enviados. Corrija os que falharam.`,
      });
    }

    setUploading(false);
  };

  const onDelete = async () => {
    setConfirmDelete(false);
    if (!selectedId) return;

    const done = await deleteItem({
      entity: 'attachment',
      id: `${selectedCourseId}_${selectedSeasonId}_${selectedMovieId}_${selectedId}`,
      successTitle: 'Material excluído',
    });

    if (done) {
      setSelectedId('');
      await loadAttachments();
    }
  };

  const selectedAttachment = attachments.find((item) => item.attachmentid === selectedId);

  return (
    <>
      <ContentEditorLayout
        moduleOptions={moduleOptions}
        moduleId={selectedSeasonId}
        onModuleChange={onModuleChange}
        modulesLoading={loadingSeasons}
        lessonOptions={lessonOptions}
        lessonId={selectedMovieId}
        onLessonChange={onLessonChange}
        lessonsLoading={loadingMovies}
        pickerItems={pickerItems}
        pickerTitle="Materiais desta aula"
        selectedItemId={selectedId}
        onSelectItem={setSelectedId}
        pickerLoading={loadingAttachments}
        editing={!!selectedId}
        dirty={false}
        saving={uploading}
        deleting={!!deletingId}
        onSave={onUpload}
        onEdit={onUpload}
        onDelete={() => setConfirmDelete(true)}
        onClear={() => {
          setSelectedId('');
          setRows([createEmptyRow()]);
        }}
      >
        {selectedAttachment && (
          <Field label="Material selecionado" full>
            <FileMeta>
              {selectedAttachment.name}
              {selectedAttachment.filesize ? ` · ${formatBytes(selectedAttachment.filesize)}` : ''}
            </FileMeta>
          </Field>
        )}

        <Field label="Arquivos" hint="PDF, Word, Excel, PowerPoint, TXT ou ZIP" full>
          <FileList>
            {rows.map((row) => (
              <div key={row.key}>
                <FileRow>
                  <HiddenFileInput
                    type="file"
                    id={`file-${row.key}`}
                    accept={ACCEPTED_EXTENSIONS}
                    onChange={(event) => onFileChange(row.key, event.target.files)}
                  />
                  <FileInputLabel htmlFor={`file-${row.key}`}>
                    <FiUpload size={14} />
                    {row.file ? 'Trocar' : 'Escolher'}
                  </FileInputLabel>

                  <TextInput
                    value={row.name}
                    onChange={(event) => updateRow(row.key, { name: event.target.value })}
                    placeholder="Nome de exibição"
                    aria-label="Nome de exibição do arquivo"
                  />
                  {row.file && <FileMeta>{formatBytes(row.file.size)}</FileMeta>}
                  {row.file && <StatusBadge status={row.status}>{STATUS_LABEL[row.status]}</StatusBadge>}

                  <RemoveButton
                    type="button"
                    onClick={() => onRemoveRow(row.key)}
                    aria-label="Remover arquivo"
                    title="Remover arquivo"
                  >
                    <FiX size={14} />
                  </RemoveButton>
                </FileRow>

                {row.status === 'uploading' && (
                  <ProgressTrack>
                    <ProgressFill progress={row.progress} />
                  </ProgressTrack>
                )}

                {row.status === 'error' && row.errorMessage && (
                  <FileMeta>{row.errorMessage}</FileMeta>
                )}
              </div>
            ))}
          </FileList>

          <AddButton type="button" onClick={onAddRow}>
            <FiPlus size={14} />
            Adicionar arquivo
          </AddButton>
        </Field>
      </ContentEditorLayout>

      <Confirm
        open={confirmDelete}
        danger
        title="Excluir material"
        description={`O material "${selectedAttachment?.name ?? ''}" some da aula. Não dá para desfazer.`}
        confirmLabel="Excluir"
        loading={!!deletingId}
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
};

export default AttachmentForm;
